import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  DEFAULT_SERVICE_CATEGORY_SECTION,
  isServiceCategorySection,
  ServiceCategory,
  ServiceCategorySection,
} from "features/serviceCategory/model/serviceCategory";

/** Справочник категорий услуг живёт по своим правилам — деревом, а не плоским списком. */
export const SERVICE_CATEGORY_CODE = "service-category";
/** Псевдо-сервис «все справочники разом». */
export const ALL_SERVICES_CODE = "all";

/**
 * Разбирает адрес вида `/{serviceCode}/{dictCode}?path=1,2&level=2&ancestorId=2`
 * и даёт навигацию по дереву категорий.
 */
export function useHandbookRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const lastSegment = location.pathname.replace(/\/+$/, "").split("/").pop();

  const [serviceCode, dictCode] = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    return [segments[0], segments[1]];
  }, [location.pathname]);

  /** Путь от корня дерева категорий до текущего узла. */
  const pathIds = useMemo(() => {
    const raw = searchParams.get("path");
    if (!raw) return [];
    return raw
      .split(",")
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));
  }, [searchParams]);

  /** Раздел категорий услуг (`?section=AGRO_CALCULATOR`); без параметра — основной. */
  const categorySection: ServiceCategorySection = useMemo(() => {
    const raw = searchParams.get("section");
    return isServiceCategorySection(raw)
      ? raw
      : DEFAULT_SERVICE_CATEGORY_SECTION;
  }, [searchParams]);

  const derivedLevel = pathIds.length;
  const derivedParentId = pathIds.length ? pathIds[pathIds.length - 1] : null;

  const [level, setLevel] = useState<number>(0);
  const [ancestorId, setAncestorId] = useState<number | null>(null);

  // `path` — источник истины; `level`/`ancestorId` в адресе поддержаны для старых ссылок.
  useEffect(() => {
    const hasPath = searchParams.has("path");

    if (hasPath) {
      setLevel(derivedLevel);
      setAncestorId(derivedParentId);
      return;
    }

    const lvl = Number(searchParams.get("level") ?? 0);
    const pidRaw = searchParams.get("ancestorId");
    const pid = pidRaw != null ? Number(pidRaw) : null;

    setLevel(Number.isFinite(lvl) ? lvl : 0);
    setAncestorId(pid != null && Number.isFinite(pid) ? pid : null);
  }, [searchParams, derivedLevel, derivedParentId]);

  const openCategory = useCallback(
    (row: ServiceCategory) => {
      if (row.type !== "CATEGORY") return;

      const nextPath = [...pathIds, Number(row.id)];
      setSearchParams((prev) => {
        const sp = new URLSearchParams(prev);
        sp.set("path", nextPath.join(","));
        sp.set("level", String(nextPath.length));
        sp.set("ancestorId", String(row.id));
        return sp;
      });
    },
    [pathIds, setSearchParams],
  );

  const goToParentCategory = useCallback(() => {
    if (pathIds.length === 0) {
      navigate(`?level=0`);
      return;
    }

    const nextPath = pathIds.slice(0, -1);
    const nextParent = nextPath.length ? nextPath[nextPath.length - 1] : null;

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      if (nextPath.length) sp.set("path", nextPath.join(","));
      else sp.delete("path");

      sp.set("level", String(nextPath.length));

      if (nextParent != null) sp.set("ancestorId", String(nextParent));
      else sp.delete("ancestorId");

      return sp;
    });
  }, [pathIds, setSearchParams, navigate]);

  const goToRootCategory = useCallback(() => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("path");
      sp.delete("level");
      sp.delete("ancestorId");
      return sp;
    });
  }, [setSearchParams]);

  /** Смена раздела начинает навигацию по дереву с корня. */
  const setCategorySection = useCallback(
    (next: ServiceCategorySection) => {
      setSearchParams((prev) => {
        const sp = new URLSearchParams(prev);
        sp.delete("path");
        sp.delete("level");
        sp.delete("ancestorId");

        if (next === DEFAULT_SERVICE_CATEGORY_SECTION) sp.delete("section");
        else sp.set("section", next);

        return sp;
      });
    },
    [setSearchParams],
  );

  return {
    pathname: location.pathname,
    serviceCode,
    dictCode,
    isCropRegion: lastSegment === "crop-region",
    isTerritorialUnit: lastSegment === "territorial-unit",
    isAgroCredit: dictCode === "agro-credit-type",
    isServiceCategory: dictCode === SERVICE_CATEGORY_CODE,
    isAllServices: serviceCode === ALL_SERVICES_CODE,
    pathIds,
    level,
    ancestorId,
    openCategory,
    goToParentCategory,
    goToRootCategory,
    categorySection,
    setCategorySection,
  };
}
